/**
 * D-EZ-93 — AGE, THE CURVE, AND WALKING AWAY.
 *
 * Sagliano's winter (SAG/5) ends careers on one number: the five lowest-placed
 * drivers and everyone who failed to finish 75% of his races lose their
 * licences. This engine capped that at exactly thirteen a winter (D-032), which
 * kept a league alive but made every career the same shape — 45% of drivers
 * lasted one season, and no star ever simply got old.
 *
 * Howard ruled on 4 Oct 2026: driver aging and retirement replaces the fixed
 * thirteen. So, HOUSE RULE, labelled as one and switchable back
 * (`OffseasonOptions.licensing: 'fixed'`):
 *
 *   • every driver has an AGE and gets a year older every winter;
 *   • a PERFORMANCE CURVE: young drivers tend to improve, drivers at their peak
 *     (about 28-36) hold, and drivers past it tend to fade;
 *   • VOLUNTARY RETIREMENT, more likely every year past the mid-thirties and
 *     less likely the more a driver is winning;
 *   • LICENCE LOSS for poor performance is kept — SAG/5's two tests still
 *     mark the drivers at risk — but as a chance rather than a fixed count, so
 *     a bad winter can clear out six and a kind one two.
 *
 * Every number in this file is a tuning constant, not a printed rule, and the
 * measured result (tenure, survivors, churn) is reported in the build notes.
 *
 * NO HIDDEN DICE. Ages are hashed from a driver's identity (no die is spent, so
 * no rating anywhere moves); every chance below is rolled on a `Dice` the
 * caller owns and records, so a winter replays exactly.
 */

import type { Dice } from './dice.ts';
import type { DriverCard } from './types.ts';

export const CITE_AGING =
  'D-EZ-93 house rule — drivers age every winter; young drivers improve, veterans fade (peak about 28-36)';
export const CITE_RETIREMENT =
  'D-EZ-93 house rule — voluntary retirement, likelier with age and less likely the more a driver is winning';
export const CITE_LICENCE_LOSS =
  'SAG/5 + D-EZ-93 — the lowest-placed and the unranked are AT RISK of losing their licence; a chance, not a fixed count';

/** The youngest and oldest a licensed driver can be. */
export const MIN_AGE = 18;
export const MAX_AGE = 55;

function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * A founding driver's age — the league is founded with a working garage, not a
 * class of rookies. 20-44, centred near 31 (three 0-8 draws on 20), which is
 * about where a professional stock car field sits.
 */
export function inauguralAge(key: string): number {
  const h = hash(`age|${key}`);
  return 20 + (h % 9) + ((h >>> 8) % 9) + ((h >>> 16) % 7);
}

/** A rookie's age when he is licensed: 19-27, most of them 21-25. */
export function rookieAge(key: string): number {
  const h = hash(`rookie-age|${key}`);
  return 19 + (h % 5) + ((h >>> 8) % 5);
}

/**
 * A driver's age. Cards carry it from the first winter on (and the 2025 scenario
 * prints real-ish ones); a card from before ages existed — a founding card, an
 * older save, a test fixture — gets a stable founding age off its identity.
 */
export function ageOf(card: Pick<DriverCard, 'driverId' | 'name' | 'age'>): number {
  if (typeof card.age === 'number' && Number.isFinite(card.age)) return card.age;
  return inauguralAge(`${card.driverId}|${card.name}`);
}

// ---------------------------------------------------------------------------
// The curve
// ---------------------------------------------------------------------------

/** Chance (0-1) a driver of this age takes a step forward this winter. */
export function growthChance(age: number): number {
  if (age <= 23) return 0.4;
  if (age <= 26) return 0.25;
  if (age <= 28) return 0.1;
  return 0;
}

/** Chance (0-1) a driver of this age loses a step this winter. */
export function declineChance(age: number): number {
  if (age <= 35) return 0;
  if (age <= 38) return 0.15;
  if (age <= 41) return 0.3;
  if (age <= 44) return 0.45;
  return 0.6;
}

export interface AgingStep {
  age: number;
  kind: 'growth' | 'decline' | 'none';
  /** What moved on the card. */
  gas: number;
  escape: number;
  /** The d100 that decided it, and the d6 that chose between gas and escape. */
  roll: number | null;
  which: number | null;
  note: string;
}

/**
 * One winter of the curve for a driver who is now `age` (already a year older).
 * A step is ONE pedal of gas or ONE point of ESCAPE — a d6 chooses which (1-3
 * gas, 4-6 escape) — inside the card's legal range. Two dice are always rolled
 * for anyone on a sloped part of the curve so the stream does not depend on the
 * result.
 */
export function agingStep(card: DriverCard, age: number, dice: Dice): AgingStep {
  const up = growthChance(age);
  const down = declineChance(age);
  if (up === 0 && down === 0) {
    return { age, kind: 'none', gas: 0, escape: 0, roll: null, which: null, note: 'in his prime — no change for age' };
  }
  const roll = dice.d(100);
  const which = dice.d6();
  const chance = up > 0 ? up : down;
  if (roll > Math.round(chance * 100)) {
    return {
      age,
      kind: 'none',
      gas: 0,
      escape: 0,
      roll,
      which,
      note: up > 0 ? `young, but no step this winter (d100 ${roll})` : `holding off the years (d100 ${roll})`,
    };
  }
  const gasFirst = which <= 3;
  if (up > 0) {
    if (gasFirst && card.gasPedal < 5) return { age, kind: 'growth', gas: 1, escape: 0, roll, which, note: `a young driver finding speed (d100 ${roll})` };
    if (card.escape < 11) return { age, kind: 'growth', gas: 0, escape: 1, roll, which, note: `a young driver learning to stay out of trouble (d100 ${roll})` };
    if (card.gasPedal < 5) return { age, kind: 'growth', gas: 1, escape: 0, roll, which, note: `a young driver finding speed (d100 ${roll})` };
    return { age, kind: 'none', gas: 0, escape: 0, roll, which, note: 'already at the top of the card' };
  }
  if (gasFirst && card.gasPedal > 2) return { age, kind: 'decline', gas: -1, escape: 0, roll, which, note: `a step slower at ${age} (d100 ${roll})` };
  if (card.escape > 3) return { age, kind: 'decline', gas: 0, escape: -1, roll, which, note: `reflexes not what they were at ${age} (d100 ${roll})` };
  if (card.gasPedal > 2) return { age, kind: 'decline', gas: -1, escape: 0, roll, which, note: `a step slower at ${age} (d100 ${roll})` };
  return { age, kind: 'none', gas: 0, escape: 0, roll, which, note: 'nothing left to lose on the card' };
}

// ---------------------------------------------------------------------------
// Walking away
// ---------------------------------------------------------------------------

/** What retirement and licence decisions read about a driver's season. */
export interface CareerStanding {
  /** Final championship rank (D-EZ-90 order). */
  rank: number;
  wins: number;
  /** Seasons completed, this one included. */
  seasons: number;
  /** SAG/5's 75% test failed on a fair sample. */
  unranked: boolean;
  /** How many drivers were ranked this season. */
  fieldSize: number;
}

/** Base chance (0-1) of retiring by age, before success is considered. */
export function baseRetirementChance(age: number): number {
  if (age <= 30) return 0.005;
  if (age <= 34) return 0.02;
  if (age <= 37) return 0.06;
  if (age <= 40) return 0.14;
  if (age <= 43) return 0.28;
  if (age <= 46) return 0.45;
  return 0.7;
}

/**
 * Voluntary retirement. Winning keeps a man in the car; a season at the back
 * of the field makes the decision for him.
 */
export function retirementChance(age: number, s: CareerStanding): number {
  let p = baseRetirementChance(age);
  if (s.rank === 1) p *= 0.4;
  else if (s.rank <= 10) p *= 0.6;
  else if (s.wins > 0) p *= 0.75;
  else if (s.rank > Math.max(30, s.fieldSize - 15)) p *= 1.6;
  if (age >= MAX_AGE) p = 1;
  return Math.min(1, p);
}

/**
 * Licence loss — SAG/5's two tests, read as RISK rather than as a quota.
 *
 *   • placed in the bottom of the ranked field (43rd and below — outside a full
 *     field — is the sharp end; 36th-42nd is at risk);
 *   • failed the 75% finishing test on a fair sample (unranked).
 *
 * A first-year driver gets some patience, a young one a little, a veteran
 * less. A race winner never loses his licence on performance.
 */
export function licenceLossChance(age: number, s: CareerStanding): number {
  if (s.wins > 0) return 0;
  let p = 0;
  if (s.rank >= 44) p = 0.25;
  else if (s.rank >= 40) p = 0.12;
  else if (s.rank >= 36) p = 0.05;
  if (s.unranked) p += 0.12;
  if (p === 0) return 0;
  if (s.seasons <= 1) p *= 0.7;
  if (age <= 25) p *= 0.8;
  else if (age >= 37) p *= 1.3;
  return Math.min(0.9, p);
}
